import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLang } from '../../../context/LangContext';
import { staggerContainer, wordReveal, SPRING } from '../../../animations/variants';

export const Hero = () => {
  const { t } = useLang();

  const title1Words = t("hero.title.1").split(" ");
  const title2 = t("hero.title.2");

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#020209] dot-grid">
      {/* Orbs */}
      <motion.div
        className="absolute rounded-full blur-[100px] opacity-25 bg-brand-green w-80 h-80 top-1/4 left-1/4"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute rounded-full blur-[80px] opacity-20 bg-brand-gold w-64 h-64 bottom-1/4 right-1/4"
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center px-4 py-2 rounded-full border border-brand-green/40 bg-brand-green/10 text-brand-green text-sm font-medium mb-8"
          data-testid="hero-badge"
        >
          {t("hero.badge")}
        </motion.div>

        {/* H1 - Word by word */}
        <motion.h1
          className="font-extrabold tracking-[-0.03em] leading-[1.05] mb-6"
          style={{ fontSize: 'clamp(48px, 8vw, 80px)' }}
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <span className="block text-white">
            {title1Words.map((word, i) => (
              <motion.span
                key={i}
                variants={wordReveal}
                transition={{ ...SPRING, delay: i * 0.08 }}
                className="inline-block mr-[0.25em]"
              >
                {word}
              </motion.span>
            ))}
          </span>
          <motion.span
            className="block gradient-text"
            variants={wordReveal}
            transition={{ ...SPRING, delay: title1Words.length * 0.08 }}
          >
            {title2}
          </motion.span>
        </motion.h1>

        {/* Subtitle - Clean, no artificial punctuation */}
        <motion.p
          className="text-neutral-400 text-lg md:text-xl max-w-2xl mx-auto mt-6 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING, delay: 0.5 }}
          data-testid="hero-subtitle"
        >
          {t("hero.subtitle")}
        </motion.p>

        {/* Chat mockup floating - No CTA buttons as requested */}
        <motion.div
          className="mt-16 mx-auto max-w-md"
          animate={{ y: [-10, 0, -10] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/[0.06]">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-neutral-400">{t("chat.status")}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-end">
                <div className="bg-brand-green/20 border border-brand-green/20 text-sm text-neutral-200 px-3 py-2 rounded-2xl rounded-tr-md max-w-[80%]">
                  {t("chat.msg1")}
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-white/[0.05] border border-white/[0.08] text-sm text-neutral-300 px-3 py-2 rounded-2xl rounded-tl-md max-w-[80%]">
                  {t("chat.msg2")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown size={24} className="text-neutral-600" />
      </motion.div>
    </section>
  );
};
